/**
 * What the Inbox AI retry says back, as text (#79). Split from the component and the motion
 * so the wording is plain data a test can read, and so the motion never has to know what a
 * "task" is.
 *
 * Three outcomes, three tones. Filed is the only good news, and it is the one that has to be
 * specific — "Created Task: Ring the dentist — in Household" lets the person confirm the
 * thing landed where they would have put it, which is the entire reason the card is held
 * open rather than vanishing the moment the model answers. A guess is not an error (the AI
 * did answer; it just wasn't sure), so it reads as a hint, not red. Failure text is already
 * a plain sentence by the time it gets here (`services/inference.py`'s `friendly`).
 */

import type { InboxAiRetryResult } from "./api";

export type Tone = "good" | "bad" | "hint";

/** One run of text. `strong` marks the lead-in ("Created Task:"), the rest is the person's
 * own words and stays plain. */
export type Segment = { text: string; strong?: boolean };

export type RetryMessage = { segments: Segment[]; tone: Tone; filed: boolean };

const KIND_LABEL: Record<string, string> = {
  task: "Task",
  note: "Note",
  project: "Project",
  context: "Context",
};

export function describeRetryResult(result: InboxAiRetryResult): RetryMessage {
  if (result.outcome === "failed") {
    return {
      segments: [{ text: result.error ?? "The AI didn't answer." }],
      tone: "bad",
      filed: false,
    };
  }

  if (result.outcome === "guess") {
    const hint = result.item.guess_kind ? KIND_LABEL[result.item.guess_kind] : undefined;
    return {
      segments: [
        {
          text:
            "Wasn't sure where this goes, so it's still in your Inbox." +
            (hint ? ` Looks like ${hint === "Context" ? "" : "a "}${hint}.` : ""),
        },
      ],
      tone: "hint",
      filed: false,
    };
  }

  const created = result.created;
  const label = created ? (KIND_LABEL[created.kind] ?? "Item") : "Item";
  const where = created?.project ?? created?.area;
  return {
    segments: [
      { text: `Created ${label}: `, strong: true },
      { text: created?.title ?? result.item.raw_text },
      ...(where ? [{ text: ` - in ${where}` }] : []),
      // A refine with a destination creates the task first and polishes it second, so the
      // task can exist while the polish failed. That is still good news, with a footnote.
      ...(result.error ? [{ text: ` (the AI couldn't polish it: ${result.error})` }] : []),
    ],
    tone: "good",
    filed: true,
  };
}

/** The same message as one string — for the screen-reader live region and the hover title. */
export function plainText(segments: Segment[]): string {
  return segments.map((segment) => segment.text).join("");
}
